import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Bell, ClipboardCheck, Check, Clock, Copy, Download, FileText, Loader2, Mail, PenLine, Pencil, Plus, Send, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { SignaturePadOverlay } from "@/components/signatures/signature-pad-overlay";
import { UseAgainDialog } from "@/components/toolbox/use-again-dialog";
import { toolboxFileHref } from "@/components/toolbox/file-attacher";
import { AssignDialog } from "@/components/toolbox/assign-dialog";
import { useFileUpload } from "@/hooks/use-file-upload";
import { apiDelete, apiGet, apiPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { purposeText, TOOLBOX_STATUS, type ToolboxMeeting, type ToolboxSigner } from "@/lib/toolbox";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/toolbox/$meetingId")({
  component: ToolboxMeetingPage,
});

const API_BASE = import.meta.env.VITE_API_URL || "";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ToolboxMeetingPage() {
  const { meetingId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useGoBack({ to: "/toolbox/library" });
  const qc = useQueryClient();
  const { upload } = useFileUpload();
  const [signing, setSigning] = useState<ToolboxSigner | null>(null);
  const [savingSignature, setSavingSignature] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [again, setAgain] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["toolbox", "meeting", meetingId],
    queryFn: () => apiGet<{ data: ToolboxMeeting }>(`/api/toolbox/${meetingId}`),
    refetchInterval: 15_000,
  });
  const m = data?.data;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["toolbox", "meeting", meetingId] });
    qc.invalidateQueries({ queryKey: ["toolbox", "list"] });
  };

  const remind = useMutation({
    mutationFn: () => apiPost<{ asked: number }>(`/api/toolbox/${meetingId}/assign`),
    onSuccess: (r) => {
      toast.success(r.asked > 0 ? `${r.asked} attendee${r.asked !== 1 ? "s were" : " was"} asked to sign` : "Nobody left to ask");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const saveTalk = useMutation({
    mutationFn: () => apiPost(`/api/toolbox/${meetingId}/save-as-talk`),
    onSuccess: () => {
      toast.success("Saved as a prepared talk");
      qc.invalidateQueries({ queryKey: ["toolbox", "talks"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const finish = useMutation({
    mutationFn: () => apiPost(`/api/toolbox/${meetingId}/complete`),
    onSuccess: () => {
      toast.success("Closed without emailing");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const clear = useMutation({
    mutationFn: (signerId: string) => apiDelete(`/api/toolbox/${meetingId}/signers/${signerId}/signature`),
    onSuccess: refresh,
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/api/toolbox/${meetingId}`),
    onSuccess: () => {
      toast.success("Meeting deleted");
      qc.invalidateQueries({ queryKey: ["toolbox", "list"] });
      navigate({ to: "/toolbox" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function onSigned(dataUrl: string) {
    const signer = signing;
    setSigning(null);
    if (!signer) return;
    setSavingSignature(signer.id);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const { fileId } = await upload(blob, { fileName: "signature.png", mimeType: "image/png", purpose: "signature" });
      await apiPost(`/api/toolbox/${meetingId}/signers/${signer.id}/sign`, { signatureFileId: fileId });
      toast.success(`Signature saved for ${signer.name}`);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save the signature");
    } finally {
      setSavingSignature(null);
    }
  }

  async function openPdf() {
    try {
      const res = await fetch(`${API_BASE}/api/toolbox/${meetingId}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not build the PDF");
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open the PDF");
    }
  }

  if (isLoading) return <div className="mx-auto max-w-lg space-y-3 pt-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-40 w-full" /></div>;
  if (isError || !m) {
    return (
      <div className="mx-auto max-w-lg pt-6 text-center">
        <p className="text-sm text-muted-foreground">This toolbox meeting could not be opened.</p>
        <Link to="/toolbox/library" search={{}} className="mt-2 inline-block text-sm text-paris-red-text underline underline-offset-2">Back to the toolbox library</Link>
      </div>
    );
  }

  const st = TOOLBOX_STATUS[m.status] ?? TOOLBOX_STATUS.draft;
  const total = m.signers.length;
  const signed = m.signers.filter((s) => s.signedAt).length;
  const finished = m.status === "sent" || m.status === "completed";
  const mine = m.signers.find((s) => s.id === m.mySignerId);

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-10">
      <div className="flex items-start gap-2 pt-2">
        <button type="button" onClick={goBack} className="mt-1 rounded p-1 hover:bg-muted" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold leading-tight text-text-primary">{m.subject}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{[m.projectName, formatDateOnly(m.date)].filter(Boolean).join(" · ")}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {(m.isPaper || !m.listHidden) && <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", st.cls)}>{m.isPaper ? "Paper copy" : st.label}</span>}
            {total > 0 && !m.listHidden && <span className="text-xs text-muted-foreground">{signed} of {total} signed</span>}
          </div>
        </div>
      </div>

      {/* The attendee's own call to sign */}
      {mine && !mine.signedAt && !finished && (
        <div className="space-y-2 rounded-xl border border-amber-400/50 bg-amber-500/10 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300"><PenLine className="h-4 w-4" /> Your signature is needed</p>
          <p className="text-xs text-muted-foreground">Confirm you attended this toolbox meeting by signing with your finger.</p>
          <Button className="w-full" onClick={() => setSigning(mine)}>Sign now</Button>
        </div>
      )}
      {mine?.signedAt && (
        <p className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400"><Check className="h-4 w-4" /> You signed this meeting.</p>
      )}

      {/* Attendance list (only for those who run meetings) */}
      {!m.listHidden && !m.isPaper && (
      <Card>
        <CardContent className="space-y-1 p-0">
          <div className="flex items-center justify-between px-4 pt-3">
            <p className="text-sm font-semibold">Attendance ({signed}/{total})</p>
            {m.canManage && !finished && total > 0 && signed < total && (
              <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={remind.isPending} onClick={() => remind.mutate()}>
                <Bell className="h-3.5 w-3.5" /> {m.status === "draft" ? "Ask to sign" : "Remind"}
              </Button>
            )}
          </div>
          {total === 0 && <p className="px-4 pb-4 pt-2 text-sm text-muted-foreground">Nobody is on the attendance list yet.</p>}
          <div className="divide-y">
            {m.signers.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.name}{s.company && <span className="ml-1 text-xs font-normal text-muted-foreground">({s.company})</span>}</p>
                  {s.signedAt ? (
                    <p className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                      <Check className="h-3 w-3" /> Signed {formatDateOnly(s.signedAt)} {s.signedMethod === "supervisor" ? "on the supervisor's device" : ""}
                    </p>
                  ) : (
                    <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400"><Clock className="h-3 w-3" /> Waiting{!s.userId ? " (no app: sign on your device)" : ""}</p>
                  )}
                </div>
                {s.signatureUrl && <img src={s.signatureUrl} alt={`${s.name}'s signature`} className="h-8 w-20 rounded border bg-white object-contain" />}
                {savingSignature === s.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {m.canManage && !finished && !s.signedAt && (
                  <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1 text-xs" onClick={() => setSigning(s)}>
                    <PenLine className="h-3.5 w-3.5" /> Sign here
                  </Button>
                )}
                {m.canManage && !finished && s.signedAt && (
                  <button type="button" className="p-1 text-muted-foreground hover:text-destructive" aria-label={`Clear ${s.name}'s signature`} title="Clear the signature to sign again"
                    onClick={() => { if (window.confirm(`Clear ${s.name}'s signature?`)) clear.mutate(s.id); }}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Ready: send it */}
      {m.canManage && m.status === "ready" && (
        <>
          <SendPanel meeting={m} onSent={refresh} />
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2"
            onClick={() => { if (window.confirm("Close this meeting without emailing it? It stays in the library and can be downloaded as a PDF.")) finish.mutate(); }}
          >
            Close without emailing
          </button>
        </>
      )}
      {/* Who received it (admins, supervisors, foremen) */}
      {!m.listHidden && !m.isPaper && (m.status === "sent" || m.status === "completed") && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="h-4 w-4" />
              {m.status === "sent" ? "Who received this toolbox" : "Not emailed"}
            </p>
            {m.status === "sent" ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Sent{m.sentAt ? ` on ${formatDateOnly(m.sentAt)}` : ""}{m.sentByName ? ` by ${m.sentByName}` : ""} to {m.sentTo.length} recipient{m.sentTo.length !== 1 ? "s" : ""}.
                </p>
                <ul className="divide-y rounded-lg border">
                  {(m.receivers && m.receivers.length > 0 ? m.receivers : m.sentTo.map((email) => ({ email, name: null, kind: null }))).map((r) => (
                    <li key={r.email} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{r.name ?? r.email}</span>
                        {r.name && <span className="block truncate text-xs text-muted-foreground">{r.email}</span>}
                      </span>
                      {r.kind && <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{r.kind}</span>}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">This toolbox was closed without emailing it, so nobody received a copy. It is in the library.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* The write-up */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setShowDetails((v) => !v)} aria-expanded={showDetails}>
            <span className="text-sm font-semibold">Meeting details</span>
            <span className="text-xs text-muted-foreground">{showDetails ? "Hide" : "Show"}</span>
          </button>
          {showDetails && (
            <div className="space-y-3 text-sm">
              <Detail label="Supervisor / foreman" value={m.foremanName} />
              <Detail label="Conducted by" value={m.conductedByName} />
              <Detail label="Purpose" value={purposeText(m.purpose, m.purposeOther)} />
              <Detail label="More details" value={m.moreDetails} />
              <Detail label="Topics discussed" value={m.topicsDiscussed} />
              <Detail label="Hazard identification / comments / questions" value={m.hazardComments} />
            </div>
          )}
        </CardContent>
      </Card>

      {(m.attachments?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-semibold">{m.isPaper ? "Paper copy" : "Photos and documents"}</p>
            <ul className="space-y-1.5">
              {m.attachments!.map((a) => (
                <li key={a.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <a href={toolboxFileHref(a.shareToken)} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-blue-700 underline underline-offset-2 dark:text-blue-400">{a.fileName}</a>
                  <a href={toolboxFileHref(a.shareToken)} download={a.fileName} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted" aria-label={`Download ${a.fileName}`}><Download className="h-4 w-4" /></a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Everyone can read and download a toolbox that was held */}
      {!m.canManage && !m.isPaper && (m.status === "ready" || finished) && (
        <Button variant="outline" size="sm" className="gap-1" onClick={openPdf}><FileText className="h-4 w-4" /> Download PDF</Button>
      )}

      {(m.canManage || m.canRun) && (
        <div className="flex flex-wrap gap-2">
          {m.canManage && !m.isPaper && <Button variant="outline" size="sm" className="gap-1" onClick={openPdf}><FileText className="h-4 w-4" /> {finished ? "Download PDF" : "Preview PDF"}</Button>}
          {!m.isPaper && <Button variant="outline" size="sm" className="gap-1" onClick={() => setAgain(true)}><Copy className="h-4 w-4" /> Use again</Button>}
          {!m.isPaper && <Button variant="outline" size="sm" className="gap-1" disabled={saveTalk.isPending} onClick={() => saveTalk.mutate()}><BookOpen className="h-4 w-4" /> Save as prepared talk</Button>}
          {m.canManage && !finished && (
            <Link to="/toolbox/new" search={{ edit: m.id }}>
              <Button variant="outline" size="sm" className="gap-1"><Pencil className="h-4 w-4" /> Edit</Button>
            </Link>
          )}
          {m.canManage && (!finished || m.isPaper) && (
            <Button variant="outline" size="sm" className="gap-1 text-destructive" onClick={() => { if (window.confirm(m.isPaper ? "Remove this paper copy from the library?" : "Delete this toolbox meeting?")) remove.mutate(); }}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      )}

      {m.canAssign && !m.isPaper && (m.status === "ready" || m.status === "sent" || m.status === "completed") && (
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setAssignOpen(true)}><ClipboardCheck className="h-4 w-4" /> Assign to sites</Button>
      )}
      <AssignDialog source={assignOpen ? { kind: "meeting", id: m.id, title: m.subject } : null} onClose={() => setAssignOpen(false)} />

      <UseAgainDialog meeting={again ? { id: m.id, subject: m.subject, projectId: m.projectId } : null} onClose={() => setAgain(false)} />

      <SignaturePadOverlay isOpen={!!signing} participantName={signing?.name ?? ""} onComplete={onSigned} onCancel={() => setSigning(null)} />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="whitespace-pre-line break-words">{value}</p>
    </div>
  );
}

/** When everyone has signed: choose who gets the PDF record by email, then send. */
function SendPanel({ meeting, onSent }: { meeting: ToolboxMeeting; onSent: () => void }) {
  const [picked, setPicked] = useState<string[]>(meeting.suggestedRecipients.map((r) => r.email.toLowerCase()));
  const [extra, setExtra] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");

  const addExtra = () => {
    const e = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) return toast.error("That email address does not look right");
    if (!picked.includes(e) && !extra.includes(e)) setExtra((x) => [...x, e]);
    setPicked((p) => (p.includes(e) ? p : [...p, e]));
    setDraft("");
  };

  const send = useMutation({
    mutationFn: () => apiPost<{ sent: number; failed: string[] }>(`/api/toolbox/${meeting.id}/send`, { recipients: picked, note }),
    onSuccess: (r) => {
      toast.success(`Sent to ${r.sent} recipient${r.sent !== 1 ? "s" : ""}${r.failed.length ? `. Could not send to: ${r.failed.join(", ")}` : ""}`);
      onSent();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dateLong = new Date(`${meeting.date}T12:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const subject = `Paris Mechanical toolbox meeting held: ${meeting.projectName ?? "the site"} - ${dateLong}`;
  const all = [...meeting.suggestedRecipients.map((r) => ({ email: r.email.toLowerCase(), label: r.name, hint: r.role })), ...extra.map((e) => ({ email: e, label: e, hint: null as string | null }))];

  return (
    <div className="space-y-3 rounded-xl border border-blue-400/40 bg-blue-500/10 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-blue-800 dark:text-blue-300"><Send className="h-4 w-4" /> Everyone signed. Send the record.</p>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Send to</p>
        {all.length === 0 && <p className="text-xs text-muted-foreground">No recipients are set on this project. Add an email below, or add contacts on the project and tick "Toolbox meetings".</p>}
        {all.map((r) => (
          <label key={r.email} htmlFor={`tb-r-${r.email}`} className="flex cursor-pointer items-center gap-3 rounded-lg border bg-background/70 px-3 py-2 text-sm">
            <input id={`tb-r-${r.email}`} type="checkbox" className="h-4 w-4" checked={picked.includes(r.email)} onChange={() => setPicked((p) => (p.includes(r.email) ? p.filter((x) => x !== r.email) : [...p, r.email]))} />
            <span className="min-w-0 flex-1 truncate">{r.label}{r.hint && <span className="ml-1 text-xs text-muted-foreground">({r.hint})</span>}</span>
            {r.label !== r.email && <span className="truncate text-xs text-muted-foreground">{r.email}</span>}
          </label>
        ))}
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} type="email" placeholder="Add an email address" className="h-10 flex-1 text-base md:text-sm" aria-label="Add an email address" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtra())} />
          <Button type="button" variant="outline" onClick={addExtra} disabled={!draft.trim()} aria-label="Add email"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Email</p>
        <div className="rounded-lg border bg-background/70 p-3 text-xs">
          <p className="font-semibold">{subject}</p>
          <p className="mt-1 text-muted-foreground">
            A toolbox meeting was held at {meeting.projectName ?? "the site"} on {dateLong}. Toolbox: {meeting.subject}. Conducted by {meeting.conductedByName}. The signed record is attached as a PDF.
          </p>
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note to the email (optional)" />
      </div>

      <Button className="w-full gap-2" disabled={send.isPending || picked.length === 0} onClick={() => send.mutate()}>
        {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 rotate-180" />}
        {send.isPending ? "Sending..." : `Send to ${picked.length} recipient${picked.length !== 1 ? "s" : ""}`}
      </Button>
    </div>
  );
}
